import {useLazyQuery} from '@apollo/client'
import {useEffect, useState} from 'react'
import {Card, Modal, Spinner} from 'react-bootstrap'
import {GET_STATE_POLICIES_DISTRIBUTION} from '../../../gql/queries/reportsQuery'
import {toast} from 'react-toastify'
import {FormattedMessage, useIntl} from 'react-intl'

interface IStatePoliciesDistribution {
  stateCode: string
  stateName: string
  policiesCount: number
  percentage: number
}

interface IResStatePoliciesDistribution {
  statePoliciesDistribution: IStatePoliciesDistribution[]
}

interface IStatePoliciesMapProps {
  dateFrom: string
  dateTo: string
  dateCriteria?: string
  carrier?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  nonCanceledStatusCodes?: string[]
  onOpenPolicies?: (extraFilters: Record<string, unknown>) => void
  refreshTrigger?: number
}

type SortField = 'state' | 'policies'
type SortOrder = 'asc' | 'desc'

export const StatePoliciesMap: React.FC<IStatePoliciesMapProps> = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: externalLoading,
  nonCanceledStatusCodes,
  onOpenPolicies,
  refreshTrigger,
}) => {
  const intl = useIntl()
  const openPoliciesForState = (stateCode: string) => {
    onOpenPolicies?.({
      stateEU: [stateCode],
      status: status && status.length > 0 ? status : nonCanceledStatusCodes,
    })
  }
  const [showModal, setShowModal] = useState(false)
  const [sortField, setSortField] = useState<SortField>('policies')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getTopStates, {data, loading, error}] = useLazyQuery<IResStatePoliciesDistribution>(
    GET_STATE_POLICIES_DISTRIBUTION,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getAllStates, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResStatePoliciesDistribution>(GET_STATE_POLICIES_DISTRIBUTION, {
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    getTopStates({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getTopStates])

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
  }, [error, intl])

  const handleShowMore = () => {
    getAllStates({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'policies' ? 'desc' : 'asc')
    }
  }

  const distribution = data?.statePoliciesDistribution || []
  const fullDistribution = fullData?.statePoliciesDistribution || []

  const sortedFullDistribution = [...fullDistribution].sort((a, b) => {
    if (sortField === 'state') {
      const comparison = a.stateName.localeCompare(b.stateName)
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.policiesCount - b.policiesCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  // Get top 10 states by policies count
  const top10States = [...distribution]
    .sort((a, b) => b.policiesCount - a.policiesCount)
    .slice(0, 10)

  const formatPercent = (num: number, digits = 2) => `${num.toFixed(digits)}%`
  const formatStateLabel = (state: IStatePoliciesDistribution) =>
    `${state.stateName} (${state.stateCode})`

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              <FormattedMessage
                id='dashboard.policies.stateMap.title'
                defaultMessage='Policies per US state (Top 10)'
              />
            </span>
            <span className='text-muted fw-semibold fs-7'>
              <FormattedMessage
                id='dashboard.policies.stateMap.subtitle'
                defaultMessage='Where your clients are located'
              />
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loading || externalLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              <FormattedMessage id='dashboard.policies.common.seeMore' defaultMessage='See more' />
            </button>
          </div>
        </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || externalLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' variant='primary' />
          </div>
        )}

        {!loading && !externalLoading && distribution.length === 0 && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <p className='text-muted'>
              <FormattedMessage
                id='dashboard.policies.common.noDataFilters'
                defaultMessage='No data available for the selected filters'
              />
            </p>
          </div>
        )}

        {!loading && !externalLoading && distribution.length > 0 && (
          <div style={{minHeight: 350}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='ps-0' style={{width: '5%'}}></th>
                  <th style={{width: '50%'}}>
                    <FormattedMessage id='common.field.state' defaultMessage='State' />
                  </th>
                  <th className='text-center' style={{width: '25%'}}>
                    <FormattedMessage
                      id='dashboard.policies.common.policies'
                      defaultMessage='Policies'
                    />
                  </th>
                  <th className='text-center pe-0' style={{width: '20%'}}>
                    <FormattedMessage
                      id='dashboard.policies.common.percentage'
                      defaultMessage='Percentage'
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {top10States.map((state, index) => (
                  <tr key={state.stateCode}>
                    <td className='ps-0 text-center'>
                      <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                    </td>
                    <td>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {formatStateLabel(state)}
                      </span>
                    </td>
                    <td className='text-center'>
                      <span
                        className='text-dark fw-bold d-block fs-6'
                        style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                        onClick={() => openPoliciesForState(state.stateCode)}
                      >
                        {state.policiesCount.toLocaleString()}
                      </span>
                    </td>
                    <td className='text-center pe-0'>
                      <span className='badge badge-light-success'>{formatPercent(state.percentage)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>

      {/* Modal for all states */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              id='dashboard.policies.stateMap.modalTitle'
              defaultMessage='Policies per US state'
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullDistribution.length === 0 && (
            <div className='text-center text-muted py-5'>
              <FormattedMessage
                id='dashboard.policies.common.noData'
                defaultMessage='No data available'
              />
            </div>
          )}
          {!loadingFull && fullDistribution.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0 text-center' style={{width: '5%'}}></th>
                    <th
                      style={{width: '50%', cursor: 'pointer'}}
                      onClick={() => handleSort('state')}
                    >
                      <FormattedMessage id='common.field.state' defaultMessage='State' />
                      {getSortIcon('state')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '25%', cursor: 'pointer'}}
                      onClick={() => handleSort('policies')}
                    >
                      <FormattedMessage
                        id='dashboard.policies.common.policies'
                        defaultMessage='Policies'
                      />
                      {getSortIcon('policies')}
                    </th>
                    <th className='text-center pe-0' style={{width: '20%'}}>
                      <FormattedMessage
                        id='dashboard.policies.common.percentage'
                        defaultMessage='Percentage'
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullDistribution.map((state, index) => (
                    <tr key={state.stateCode}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                          {formatStateLabel(state)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='text-dark fw-bold d-block fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesForState(state.stateCode)}
                        >
                          {state.policiesCount.toLocaleString()}
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span className='badge badge-light-success'>
                          {formatPercent(state.percentage)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
